import type { Comparison, PeriodEcho } from '../creatives/contract'
import type { Lang } from '../i18n'

/** The presets the period control offers, each ending on the last complete day. */
export type PresetDays = 7 | 14 | 30 | 60 | 90

export const PRESET_DAYS: readonly PresetDays[] = [7, 14, 30, 60, 90]

/** The presets the pre-GRO-65 server answers, and so the only ones offered while the
 * shell is talking to it. */
export const OLD_CONTRACT_PRESETS: readonly PresetDays[] = [14, 30, 90]

/**
 * The window the reader asked for: a preset the server resolves against its own clock,
 * or two calendar days, inclusive, as `YYYY-MM-DD` strings.
 */
export type Period =
  | { kind: 'preset'; days: PresetDays }
  | { kind: 'range'; from: string; to: string }

export const DEFAULT_PERIOD: Period = { kind: 'preset', days: 14 }

export function isPresetDays(value: unknown): value is PresetDays {
  return (PRESET_DAYS as readonly unknown[]).includes(value)
}

/** The query parameters that ask for the window. */
export function periodQuery(period: Period): Record<string, string> {
  return period.kind === 'preset'
    ? { period: String(period.days) }
    : { from: period.from, to: period.to }
}

/** A stable key for the window, for effect dependencies and scope keys. */
export function periodKey(period: Period): string {
  return period.kind === 'preset' ? `p${period.days}` : `r${period.from}_${period.to}`
}

// Calendar arithmetic on the three integers of a `YYYY-MM-DD` string, through `Date.UTC`,
// which has no daylight-saving hour. No `Date` is ever read in the browser's own zone, so
// the answer does not depend on where the reader sits.
function utcMillis(date: string): number {
  const [y, m, d] = date.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

const DAY_MS = 86_400_000

/** Days from `from` to `to`, both counted. */
export function daysBetween(from: string, to: string): number {
  return Math.round((utcMillis(to) - utcMillis(from)) / DAY_MS) + 1
}

/** The calendar day `n` days after `date`; negative `n` goes back. */
export function addDays(date: string, n: number): string {
  const moved = new Date(utcMillis(date) + n * DAY_MS)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${moved.getUTCFullYear()}-${pad(moved.getUTCMonth() + 1)}-${pad(moved.getUTCDate())}`
}

/** How many days the window covers. */
export function periodDays(period: Period): number {
  return period.kind === 'preset' ? period.days : daysBetween(period.from, period.to)
}

/**
 * The whole echo rule of the contract, in one place. A preset matches its own `preset`
 * echo; a range matches its dates, because a 14-day preset and a 14-day range are
 * different windows. A comparing request expects a comparison and a plain one expects
 * none. When the view has already fixed its window (`parent`), a related response must
 * cover exactly those dates too: a preset resolved either side of midnight echoes the
 * same `preset` over a different window.
 */
export function matchesEcho(
  ask: { period: Period; compare: boolean },
  response: { period: PeriodEcho; comparison: Comparison | null },
  parent?: { from: string; to: string },
): boolean {
  const echo = response.period
  const periodMatches = ask.period.kind === 'preset'
    ? echo.preset === ask.period.days
    : echo.preset === null && echo.from === ask.period.from && echo.to === ask.period.to
  if (!periodMatches) return false
  if ((response.comparison !== null) !== ask.compare) return false
  if (parent && (echo.from !== parent.from || echo.to !== parent.to)) return false
  return true
}

/** "1 - 14 Sep" / "1 - 14 сент.", with the month on the first day only when they differ. */
const RANGE_LOCALE: Record<Lang, string> = { en: 'en-GB', ru: 'ru-RU', uk: 'uk-UA' }

interface RangeFormatters {
  day: Intl.DateTimeFormat
  dayMonth: Intl.DateTimeFormat
  withYear: Intl.DateTimeFormat
}

// The first construction for a locale loads its data, about 6 ms (GRO-180), so each
// locale's three formatters are built once.
const rangeCache = new Map<string, RangeFormatters>()

function rangeFormatters(lang: Lang): RangeFormatters {
  const locale = RANGE_LOCALE[lang]
  let formatters = rangeCache.get(locale)
  if (formatters === undefined) {
    formatters = {
      day: new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }),
      dayMonth: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }),
      withYear: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }),
    }
    rangeCache.set(locale, formatters)
  }
  return formatters
}

/**
 * Builds the range formatters for `lang` ahead of the first `formatRange`. App calls it
 * in an effect, after the screens' effects have sent their requests, so the cost lands
 * while the response is on the network and not in the task that renders it.
 */
export function warmRangeFormatters(lang: Lang): void {
  rangeFormatters(lang)
}

export function formatRange(from: string, to: string, lang: Lang): string {
  const { day, dayMonth, withYear } = rangeFormatters(lang)
  const [fy, fm] = from.split('-')
  const [ty, tm] = to.split('-')
  const start = new Date(utcMillis(from))
  const end = new Date(utcMillis(to))
  if (fy !== ty) return `${withYear.format(start)} - ${withYear.format(end)}`
  if (fm !== tm) return `${dayMonth.format(start)} - ${dayMonth.format(end)}`
  if (from === to) return dayMonth.format(end)
  return `${day.format(start)} - ${dayMonth.format(end)}`
}
